import {Alert, Button, Group, Input, Stack, Text} from '@mantine/core';
import React, {useState, useCallback} from 'react';

import {assert} from 'Asserts';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import {createFilesFromUpload} from 'files/network/FileDAO';
import notifications from 'notifications';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import FileChooser from 'ui-library/FileChooser';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';
import ModalFooter from 'ui-library/modals/ModalFooter';
import type {RunModalProps} from 'ui-library/runModal';
import {CenteredSpinner} from 'ui-library/Spinner';
import {upload} from 'util/ImportUtil';
import {getUserMessage} from 'util/Net';

const ROOT_TEST_PROPS = getTestProps({component: 'CreateFileModal', element: 'root'});

export interface CreateFileModalProps extends RunModalProps<Array<string>> {
  initialFolderId?: string;
  /**
   * Any provided value must be at least 1. If it is equal to 1, the file selector will be single-select.
   */
  maxItems?: number;
}

function getErrorMessage(reason: unknown): string | null {
  // Server errors: invalid extension, S3 failures.
  const serverMessage = getUserMessage(reason);
  if (serverMessage != null) {
    return serverMessage;
  }
  // Locally-constructed rejections from ImportUtil.upload (size limit, etc.)
  // reject with a plain {userMessage} object that getUserMessage's skips.
  if (reason != null) {
    const userMessage = (reason as {userMessage?: unknown}).userMessage;
    if (typeof userMessage === 'string') {
      return userMessage;
    }
  }
  return null;
}

export default function CreateFileModal({
  close,
  initialFolderId,
  maxItems,
}: CreateFileModalProps): JSX.Element {
  assert(
    maxItems == null || maxItems >= 1,
    `CreateFileModal: max number of items must be >= 1 or undefined, got ${maxItems}`
  );

  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);

  const onClose = useCallback(() => {
    close([]);
  }, [close]);

  const onSave: onSaveCallback = useCallback(
    async ({folderId, selectedFiles}) => {
      try {
        if (selectedFiles.length === 0) {
          throw new Error('No files selected.');
        }

        // Phase 1: Upload all files to S3 via ImportUtil.upload()
        setLoadingMessage('Uploading files...');

        const uploadResults = await Promise.allSettled(
          selectedFiles.map((file) => Promise.resolve(upload(file)))
        );
        const succeeded: Array<{file: File; s3Location: string}> = [];
        const failures: Array<{name: string; message: string | null}> = [];

        uploadResults.forEach((result, i) => {
          if (result.status === 'fulfilled') {
            succeeded.push({file: selectedFiles[i], s3Location: result.value!});
          } else {
            failures.push({name: selectedFiles[i].name, message: getErrorMessage(result.reason)});
          }
        });
        if (failures.length > 0) {
          failures.forEach(({name, message}) => {
            notifications.error(`Failed to upload: ${name}: ${message ?? 'Unknown error'}`);
          });
        }
        if (succeeded.length === 0) {
          // Exit early if no files were uploaded
          close([]);
          return;
        }

        // Phase 2: Create file records in a single transactional request
        setLoadingMessage('Creating file records...');
        const response = await createFilesFromUpload(
          succeeded.map(({file, s3Location}) => ({
            name: file.name,
            filename: file.name,
            folderId,
            s3Location,
          }))
        );

        FolderDAOCache.invalidateFolderContents(folderId);
        notifications.success(
          `Created ${response.createdIds.length} file${response.createdIds.length === 1 ? '' : 's'}`
        );

        close(response.createdIds);
        // This `catch` handles notifying the user and closing the modal
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (error) {
        notifications.error(getErrorMessage(error) ?? 'Failed to create file records.');
        close([]);
      }
    },
    [close]
  );

  return loadingMessage ? (
    <Stack spacing="md" py="xl" align="center" {...ROOT_TEST_PROPS}>
      <CenteredSpinner aria-label="Processing files" />
      <Text size="sm" color="dimmed">
        {loadingMessage}
      </Text>
    </Stack>
  ) : (
    <CreateFileModalConfigurationForm
      initialFolderId={initialFolderId}
      maxItems={maxItems}
      onClose={onClose}
      onSave={onSave}
    />
  );
}

type onSaveCallback = (args: {folderId: string; selectedFiles: Array<File>}) => Promise<void>;

interface ConfigurationPhaseProps {
  initialFolderId?: string;
  maxItems?: number;
  onClose: () => void;
  onSave: onSaveCallback;
}

function CreateFileModalConfigurationForm({
  initialFolderId,
  maxItems,
  onClose,
  onSave,
}: ConfigurationPhaseProps): JSX.Element {
  const [folderId, setFolderId] = useState<string | null>(initialFolderId ?? null);
  const [selectedFiles, setSelectedFiles] = useState<Array<File>>([]);

  const isBlockedByMaxItems = maxItems != null && selectedFiles.length >= maxItems;
  const canSubmit = folderId != null && selectedFiles.length > 0;

  function handleSubmit(): void {
    if (folderId == null || selectedFiles.length === 0) {
      return;
    }
    onSave({folderId, selectedFiles});
  }

  return (
    <Stack spacing="md" {...ROOT_TEST_PROPS}>
      <Input.Wrapper label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} required={true}>
        <FolderSelector
          enableRedesign={true}
          level="append"
          // This prop allows us to select non-root folders
          showNotebook={true}
          value={folderId}
          onChange={(e) => {
            setFolderId(e.target.value);
          }}
          unsetOnFetchFail={true}
          selectorModalZIndex={ZINDEX_FLOATING_UI as 1060}
        />
      </Input.Wrapper>
      <Input.Wrapper label="File" required={true}>
        {isBlockedByMaxItems ? (
          <Alert variant="warning">
            At most {maxItems} file{maxItems === 1 ? '' : 's'} can be selected. Remove existing files to add
            others.
          </Alert>
        ) : (
          <FileChooser
            multiple={maxItems == null || maxItems > 1}
            onSelectFiles={(...files: Array<File>) => {
              setSelectedFiles((prev) => {
                const existingNames = new Set(prev.map((f) => f.name));
                // It is possible for the user to drag and drop multiple files with the same name.
                // We will filter out the duplicates and notify the user that files were skipped.
                const newFiles = files.filter((f) => {
                  if (existingNames.has(f.name)) {
                    notifications.warn(`File ${f.name} already exists. Skipping.`);
                    return false;
                  }
                  existingNames.add(f.name);
                  return true;
                });
                const merged = [...prev, ...newFiles];
                if (maxItems != null && merged.length > maxItems) {
                  notifications.warn(`At most ${maxItems} file${maxItems === 1 ? '' : 's'} can be selected.`);
                  return merged.slice(0, maxItems);
                }
                return merged;
              });
            }}
          />
        )}
        {selectedFiles.length > 0 && (
          <FileList>
            {selectedFiles.map((file) => (
              <FileRow
                key={file.name}
                file={file}
                onRequestRemove={() => {
                  setSelectedFiles(selectedFiles.filter((f) => f !== file));
                }}
              />
            ))}
          </FileList>
        )}
      </Input.Wrapper>
      <ModalFooter>
        <Group position="right">
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!canSubmit} onClick={handleSubmit}>
            Create
          </Button>
        </Group>
      </ModalFooter>
    </Stack>
  );
}
